import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useFieldArray, useForm } from "react-hook-form";
import { getUser } from "../../../../pages/userSlice";

import {
  CreateCredential,
  UpdateCredential,
} from "../../../../services/user/infonavit/adminCredentials";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CODES } from "../../../../consts/codes";
import ModalInfo from "../../Modals/modalInfo";
import { REGEXP } from "../../../../consts/regexp";

import { CredentialsForm } from "./credentialsForm";

const AddCredentials = ({
  fetchCredentials,
  openEditCredentials,
  setOpenEditCredentials,
  setCurrentCredentials,
  currentCredentials = null,
}) => {
  /**
   * Use Selector
   */
  const userInfo = useSelector(getUser);

  const isEditForm = currentCredentials !== null;

  /**
   * Title Container component
   */
  const DialogTitleContainer = (props) => {
    const { children, onClose, ...other } = props;

    return (
      <DialogTitle {...other}>
        {children}
        {onClose ? (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>
    );
  };

  /**
   * useTranslation
   */
  const { t } = useTranslation();

  /**
   * Schema
   */
  const schema = yup.object().shape({
    companyName: yup
      .string()
      .max(30, t("App.validationMessages.maxCharacters", { number: 30 }))
      .required(t("App.validationMessages.required"))
      .matches(REGEXP.ONLY_LETTERS, t("App.validationMessages.onlyString")),
    rfc: yup
      .string()
      .min(12, t("App.validationMessages.minCharacters", { number: 12 }))
      .max(13, t("App.validationMessages.maxCharacters", { number: 13 }))
      .required(t("App.validationMessages.required"))
      .matches(REGEXP.INFONAVIT.RFC, t("App.validationMessages.infonavit.rfc")),
    rfcPKey: yup
      .string()
      .min(8, t("App.validationMessages.minCharacters", { number: 8 }))
      .max(30, t("App.validationMessages.maxCharacters", { number: 30 }))
      .required(t("App.validationMessages.required")),

    nrps: yup.array().of(
      yup
        .string()
        .nullable()
        .notRequired()
        .min(11, t("App.validationMessages.minCharacters", { number: 11 }))
        .max(11, t("App.validationMessages.maxCharacters", { number: 11 }))
        .matches(
          REGEXP.INFONAVIT.NRP,
          t("App.validationMessages.infonavit.nrp")
        )
        .transform((value) => (!!value ? value : null))
    ),
  });

  /**
   * useForm
   */
  const {
    handleSubmit,
    setValue,
    reset,
    getValues,
    watch,
    formState: { errors },
    control,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      nrps: [""],
      companyName: "",
      rfc: "",
      rfcPKey: "",
    },
  });

  /**
   * useFieldArray que maneja el arreglo de NRPs
   */
  const {
    fields: nrpsFields,
    append: permissionsAppend,
    remove: permissionsRemove,
    update: permissionsUpdate,
  } = useFieldArray({
    control,
    name: "nrps",
  });

  /**
   * Use State
   */

  const [openCreateUser, setOpenCreateUser] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [isDesktop, setDesktop] = useState(window.innerWidth > 576);

  const [openModalInfo, setOpenModalInfo] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [createUserData, setCreateUserData] = useState({});

  /**
   * Use Effect
   */

  /**
   * useEffect para actualizar el tamaño de pantalla actual de la pantalla
   */
  useEffect(() => {
    window.addEventListener("resize", updateMedia);
    return () => window.removeEventListener("resize", updateMedia);
  });

  /**
   * Carga la información de las credenciales a editar
   */
  useEffect(() => {
    if (isEditForm) {
      setValue("companyName", currentCredentials?.companyName);
      setValue(
        "nrps",
        currentCredentials?.nrps?.length > 0 ? currentCredentials?.nrps : [""]
      );
      setValue("rfc", currentCredentials?.rfc);
      setValue("rfcPKey", currentCredentials?.password);
    }
  }, [currentCredentials, isEditForm]);

  /**
   * Handles
   */

  /**
   * Función para añadir un permiso
   */
  const handleAddNewNrp = () => {
    permissionsAppend("");
  };

  /**
   *
   * Función para remover un permiso del arreglo de permisos
   *
   * @param {String} id
   * @param {Number} index
   */
  const handleRemoveNrp = (index) => {
    permissionsRemove(index);
  };

  /**
   * Función que abre el modal
   */
  const handleOpen = () => {
    reset();
    setOpenCreateUser(true);
  };

  /**
   * Función para cerrar el modal
   */
  const handleClose = async () => {
    isEditForm ? setOpenEditCredentials(false) : setOpenCreateUser(false);

    if (isEditForm) setCurrentCredentials({});

    setLoadingCreate(false);
    reset();
  };

  /**
   * Método para actualizar el tamaño de pantalla actual
   */
  const updateMedia = () => {
    setDesktop(window.innerWidth > 490);
  };

  /**
   *
   * Función que construye el objeto de usuario
   *
   * @param {Object} data información del usuario
   * @returns objeto de usuario según formato
   */
  const handleCreateCredentialsPayload = (data) => {
    const payload = {
      ...data,
      mainUser: userInfo.mainUser,
      nrps: data.nrps.filter((nrp) => !!nrp),
      password: data?.rfcPKey,
      pk: currentCredentials?.pk ?? null,
      sk: currentCredentials?.sk ?? null,
    };

    return payload;
  };

  /**
   * Función que retorna el título del modal según código de respuesta
   * @param {Number} responseCode código de respuesta
   * @returns título según código
   */
  const handleModalTitle = (responseCode) => {
    if (responseCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST) {
      return "";
    } else if (responseCode === 1001) {
      return t("App.userDetail.modalTitle.title2");
    } else if (responseCode === 1002) {
      return t("App.userDetail.modalTitle.title1");
    } else {
      return "";
    }
  };

  /**
   *
   * Function to handle form submit
   *
   * @param {Object} data
   */
  const handleFormSubmit = async (data) => {
    return isEditForm
      ? handleEditCredentials(data)
      : handleCreateCredentials(data);
  };

  /**
   *
   * Function to edit credentials
   *
   * @param {Object} data
   */
  const handleEditCredentials = async (data) => {
    try {
      const credentialsPayload = handleCreateCredentialsPayload(data);
      setLoadingCreate(true);
      const {
        status,
        data: { responseStatus, response },
      } = await UpdateCredential(credentialsPayload);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        fetchCredentials();
      }

      setModalTitle(handleModalTitle(responseStatus.statusCode));
      setOpenModalInfo(true);
      setCreateUserData({ data: { responseStatus, response }, status });

      setLoadingCreate(false);
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    }
  };

  /**
   *
   * Function to create a new credentials
   *
   * @param {Object} data
   */
  const handleCreateCredentials = async (data) => {
    try {
      const credentialsPayload = handleCreateCredentialsPayload(data);
      setLoadingCreate(true);
      const {
        status,
        data: { responseStatus, response },
      } = await CreateCredential(credentialsPayload);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        fetchCredentials();
      }

      setModalTitle(handleModalTitle(responseStatus.statusCode));
      setOpenModalInfo(true);
      setCreateUserData({ data: { responseStatus, response }, status });

      setLoadingCreate(false);
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    }
  };

  return (
    <>
      {!isEditForm ? (
        <Button
          startIcon={<AddIcon sx={{ fontSize: 22 }} />}
          className="btn__filled__blue btn__applyFilter"
          onClick={handleOpen}
        >
          {t("App.infonavitUploadCredentials.addBtn")}
        </Button>
      ) : (
        ""
      )}

      <Dialog
        onClose={handleClose}
        open={isEditForm ? openEditCredentials : openCreateUser}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              width: "100%",
              height: "100%",
              maxWidth: "900px",
              maxHeight: "486px",
              boxShadow: "none",
              borderRadius: "22px",
              padding: "2rem",
            },
          },
        }}
      >
        <DialogTitleContainer onClose={handleClose}>
          <Grid container direction="column" rowSpacing={1}>
            <Grid item className="display-large__primary-one">
              {isEditForm
                ? t("App.infonavitUploadCredentials.formModal.editTitle")
                : t("App.infonavitUploadCredentials.formModal.createTitle")}
            </Grid>
          </Grid>
        </DialogTitleContainer>

        <DialogContent
          className="scroll-table"
          sx={{ padding: { xs: 0, sm: "2.25rem" } }}
        >
          <form
            id="form-nrps"
            autocomplete="off"
            onSubmit={handleSubmit(handleFormSubmit)}
          >
            <CredentialsForm
              isEditForm={isEditForm}
              control={control}
              errors={errors}
              nrpsFields={nrpsFields}
              handleAddNewNrp={handleAddNewNrp}
              handleRemoveNrp={handleRemoveNrp}
              getValues={getValues}
              setValue={setValue}
              t={t}
            />
          </form>
        </DialogContent>

        <DialogActions>
          <Grid
            container
            direction={isDesktop ? "row" : "column-reverse"}
            className="col-flex"
            columnGap={4}
            sx={{ padding: "1.5%" }}
          >
            <Grid item xs={12} sm={2} lg={1} className="full-width">
              <Button
                className="btn__filled__gradient full-width"
                onClick={handleClose}
              >
                {t("App.buttonLabels.cancel")}
              </Button>
            </Grid>
            <Grid item xs={12} sm={4} lg={2.2} className="full-width">
              <Button
                className="btn__applyFilter"
                type="submit"
                form="form-nrps"
                startIcon={!loadingCreate ? <AddIcon /> : <></>}
              >
                {!loadingCreate ? (
                  isEditForm ? (
                    t("App.infonavitUploadCredentials.formModal.editBtn")
                  ) : (
                    t("App.infonavitUploadCredentials.formModal.createBtn")
                  )
                ) : (
                  <CircularProgress size={21} color="inherit" />
                )}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>

      <ModalInfo
        title={modalTitle}
        responseData={createUserData}
        onClose={() => {
          handleClose();
          setOpenModalInfo(false);
        }}
        open={openModalInfo}
      />
    </>
  );
};

export default AddCredentials;
